'use client';

import { useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { RotateCw, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { UPLOAD_ATTENTION_TONE_CLASS } from '@/components/org-chat/pending-upload-send/pending-upload-send-file-row';
import {
  formatFolderUploadPanelCopy as fill,
  type FolderUploadPanelCopy,
} from '@/i18n/folder-upload-panel-copy';
import type { FolderUploadFileState } from '@/lib/uploads/folder-upload/types';
import { cn } from '@/lib/utils';

import { folderUploadRowView } from './folder-upload-panel-view';

const ROW_HEIGHT = 44;
/** Rows drawn before the list has measured itself (and in tests). */
const INITIAL_RECT = { width: 352, height: 264 };

/**
 * Every file of an upload, virtualised so a 1,600-file folder stays
 * smooth. Each row names the file, its state or failure reason, and its
 * own Cancel or Retry.
 */
export function FolderUploadFileList({
  rows,
  copy,
  locale,
  onCancel,
  onRetry,
  holdRetry = false,
}: {
  rows: ReadonlyArray<FolderUploadFileState>;
  copy: FolderUploadPanelCopy;
  locale: string;
  onCancel: (fileId: number) => void;
  onRetry: (fileId: number) => void;
  /** Hide per-file Retry while the upload is still running. */
  holdRetry?: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 8,
    initialRect: INITIAL_RECT,
  });
  return (
    <div
      ref={scrollRef}
      role="list"
      className="-mx-1 max-h-66 overflow-y-auto overscroll-contain"
    >
      <div
        className="relative w-full"
        style={{ height: virtualizer.getTotalSize() }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const file = rows[virtualRow.index];
          if (!file) return null;
          const view = folderUploadRowView(file, copy, locale);
          const location = file.relativeDirectoryPath
            ? `${file.relativeDirectoryPath}/${file.name}`
            : file.name;
          return (
            <div
              key={file.id}
              role="listitem"
              data-folder-upload-row={file.status}
              className="absolute inset-x-0 flex items-center gap-1 px-1"
              style={{
                height: ROW_HEIGHT,
                transform: `translateY(${virtualRow.start}px)`,
              }}
            >
              <div className="min-w-0 flex-1">
                <p className="type-meta-strong truncate" title={location}>
                  {file.name}
                </p>
                <p
                  className={cn(
                    'type-meta truncate tabular-nums',
                    view.tone === 'attention'
                      ? UPLOAD_ATTENTION_TONE_CLASS
                      : 'text-muted-foreground'
                  )}
                >
                  {view.detail}
                  {file.relativeDirectoryPath ? (
                    <span className="text-muted-foreground">
                      {' · '}
                      {file.relativeDirectoryPath}
                    </span>
                  ) : null}
                </p>
              </div>
              {view.canCancel ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground shrink-0"
                  aria-label={fill(copy.cancelFile, { name: file.name })}
                  title={fill(copy.cancelFile, { name: file.name })}
                  onClick={() => onCancel(file.id)}
                >
                  <X aria-hidden className="size-3.5" />
                </Button>
              ) : null}
              {view.canRetry && !holdRetry ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="text-muted-foreground shrink-0"
                  aria-label={fill(copy.retryFile, { name: file.name })}
                  title={fill(copy.retryFile, { name: file.name })}
                  onClick={() => onRetry(file.id)}
                >
                  <RotateCw aria-hidden className="size-3.5" />
                </Button>
              ) : !view.canCancel ? (
                // One stable action column: a row without an action keeps
                // the same slot, so every row's text ends at the same edge.
                <span aria-hidden className="size-8 shrink-0" />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
